import React from 'react';

import {
  Box,
  IconButton,
  InputAdornment,
  TextField as MuiTextField,
  TextFieldProps as MuiTextFieldProps,
} from '@mui/material';
import { inputBaseClasses } from '@mui/material/InputBase';
import { inputLabelClasses } from '@mui/material/InputLabel';
import { styled, useTheme } from '@mui/material/styles';

import { tokens } from '../../theme/styles/themeMui';
import { Tooltip } from '../Tooltip/Tooltip';
import { ClearIcon } from './icons/Clear';
import { ErrorIcon } from './icons/Error';
import { LockIcon } from './icons/LockIcon';
import { LockStrongIcon } from './icons/LockStrongIcon';
import { ViewIcon } from './icons/ViewIcon';
import { ViewOffIcon } from './icons/ViewOffIcon';
import { checkPasswordStrength, PasswordComplexity } from './utils/password';

const KalepTextField = styled(MuiTextField)<MuiTextFieldProps>(({ theme, size, fullWidth }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '8px',
  width: fullWidth ? '100%' : 'min(400px, 100%)',
  [`& .MuiInputLabel-root`]: {
    ...tokens.typography['body-s-accent'],
    position: 'unset',
    transform: 'none',
    color: theme.palette.colors.content.primary,
  },
  [`& .${inputLabelClasses.root}.Mui-focused`]: {
    color: theme.palette.colors.content.primary,
  },
  [`& .${inputLabelClasses.root}.Mui-error`]: {
    color: theme.palette.colors.content.primary,
  },
  [`& .${inputBaseClasses.root}`]: {
    width: '100%',
    display: 'flex',

    ...(size === 'small' && {
      padding: '8px',
      gap: '8px',
    }),
    ...(size === 'medium' && {
      padding: '12px',
      gap: '12px',
    }),
    ...(size === 'large' && {
      padding: '16px',
      gap: '12px',
    }),
  },
  [`& .${inputBaseClasses.root} fieldset`]: {
    borderRadius: '8px',
    borderWidth: '2px',
    borderColor: theme.palette.colors.special.nulled,
    background: theme.palette.colors.bg['neutral-secondary'],
  },
  [`& .${inputBaseClasses.root}.Mui-focused fieldset`]: {
    background: theme.palette.colors.special.nulled,
  },
  [`& .${inputBaseClasses.root}:not(.Mui-error):hover fieldset`]: {
    borderColor: theme.palette.colors.border['action-primary'],
  },
  [`& .${inputBaseClasses.root}.Mui-error fieldset`]: {
    borderColor: theme.palette.colors.border['danger-primary'],
  },
  [`& fieldset legend`]: {
    visibility: 'hidden',
    width: 0,
    overflow: 'hidden',
    padding: 0,
    display: 'none',
  },
  [`& .MuiOutlinedInput-notchedOutline`]: {
    top: 0,
  },
  [`& .${inputBaseClasses.root} input`]: {
    padding: '0px',
    color: theme.palette.colors.content.primary,
    ...(size === 'small' && {
      height: '20px',
      ...tokens.typography.bodySRegular,
    }),
    ...(size === 'medium' && {
      height: '24px',
      ...tokens.typography.bodyMRegular,
    }),
    ...(size === 'large' && {
      height: '24px',
      ...tokens.typography.bodyMRegular,
    }),
  },
  [`& .MuiFormHelperText-root`]: {
    marginLeft: '0px',
    ...tokens.typography.bodySRegular,
    color: theme.palette.colors.content.secondary,
  },
  [`& .MuiSvgIcon-root`]: {
    ...(size === 'small' && {
      height: '20px',
      width: '20px',
    }),
    ...(size === 'medium' && {
      height: '24px',
      width: '24px',
    }),
    ...(size === 'large' && {
      height: '24px',
      width: '24px',
    }),
  },
  [`& .MuiInputAdornment-root`]: {
    marginLeft: '0px',
    color: theme.palette.colors.content.tertiary,
  },
  [`& .MuiInputAdornment-root .MuiIconButton-root`]: {
    padding: 0,
    marginRight: '0px',
  },
}));

export type TextFieldProps = MuiTextFieldProps & {
  clearButtonTooltipText?: string;
  showPasswordTooltipText?: string;
  hidePasswordTooltipText?: string;
  weakPasswordMessage?: string;
  mediumPasswordMessage?: string;
  strongPasswordMessage?: string;
  /** Optional icon rendered inside the input on the leading side. Wrapped in an InputAdornment. */
  startIcon?: React.ReactNode;
};

export function TextField(props: TextFieldProps) {
  const {
    value: propValue,
    onFocus,
    onBlur,
    onChange,
    slotProps,
    clearButtonTooltipText = 'Clear',
    showPasswordTooltipText = 'Show password',
    hidePasswordTooltipText = 'Hide password',
    weakPasswordMessage,
    mediumPasswordMessage,
    strongPasswordMessage,
    startIcon,
    name,
    ...rest
  } = props;
  const [isFocused, setFocused] = React.useState(false);
  const [internalValue, setInternalValue] = React.useState(propValue ?? '');
  const [shouldShowPassword, setShowPassword] = React.useState(false);
  const [complexity, setComplexity] = React.useState<PasswordComplexity | null>(null);

  const value = propValue !== undefined ? propValue : internalValue;

  const theme = useTheme();

  React.useEffect(() => {
    if (propValue !== undefined) {
      const valueAsString = String(propValue ?? '');
      setInternalValue(valueAsString);
      if (props.type === 'password') {
        setComplexity(
          checkPasswordStrength(
            valueAsString,
            weakPasswordMessage,
            strongPasswordMessage,
            mediumPasswordMessage,
          ),
        );
      }
    }
  }, [propValue, props.type, weakPasswordMessage, strongPasswordMessage, mediumPasswordMessage]);

  const handleFocus = React.useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(true);
      onFocus?.(e);
    },
    [onFocus],
  );

  const handleBlur = React.useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setFocused(false);
      onBlur?.(e);
    },
    [onBlur],
  );

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInternalValue(e.target.value);
      onChange?.(e);
      if (props.type === 'password') {
        setComplexity(
          checkPasswordStrength(
            e.target.value,
            weakPasswordMessage,
            strongPasswordMessage,
            mediumPasswordMessage,
          ),
        );
      }
    },
    [onChange, props.type, weakPasswordMessage, strongPasswordMessage, mediumPasswordMessage],
  );

  const handleClear = React.useCallback(() => {
    const syntheticEvent = {
      target: { value: '', name: name ?? '' },
    } as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
    setInternalValue('');
    onChange?.(syntheticEvent);
  }, [onChange, name]);

  const handleMouseDownClear = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  }, []);

  const handleTogglePasswordVisibility = React.useCallback(() => {
    setShowPassword((show) => !show);
  }, []);

  const shouldShowClear = isFocused && String(value).length > 0;

  const fieldType = shouldShowPassword ? 'text' : props.type;

  const isError = props.error || complexity?.strength === 'weak';
  const helperText =
    props.helperText ??
    (props.type === 'password' ? (
      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {complexity?.strength === 'strong' ? (
          <LockStrongIcon />
        ) : (
          <LockIcon
            color={
              complexity?.strength === 'medium'
                ? theme.palette.colors.content['warning-secondary']
                : undefined
            }
          />
        )}
        {complexity?.message ||
          'Password must be at least 8 characters long and include uppercase, lowercase, numbers, and special characters.'}
      </Box>
    ) : undefined);

  return (
    <KalepTextField
      {...rest}
      name={name}
      type={fieldType}
      value={value}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      error={isError}
      helperText={helperText}
      slotProps={{
        ...slotProps,
        input: {
          ...slotProps?.input,
          ...(startIcon && {
            startAdornment: <InputAdornment position="start">{startIcon}</InputAdornment>,
          }),
          endAdornment: (
            <>
              {shouldShowClear && (
                <InputAdornment position="end">
                  <Tooltip title={clearButtonTooltipText}>
                    <IconButton
                      onClick={handleClear}
                      onMouseDown={handleMouseDownClear}
                      edge="end"
                      aria-label="clear"
                    >
                      <ClearIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              )}
              {props.type === 'password' && (
                <InputAdornment position="end">
                  <Tooltip
                    title={shouldShowPassword ? hidePasswordTooltipText : showPasswordTooltipText}
                  >
                    <IconButton
                      onClick={handleTogglePasswordVisibility}
                      onMouseDown={handleMouseDownClear}
                      edge="end"
                      aria-label="toggle password visibility"
                    >
                      {shouldShowPassword ? <ViewOffIcon /> : <ViewIcon />}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              )}
              {props.error && (
                <InputAdornment position="end">
                  <ErrorIcon />
                </InputAdornment>
              )}
            </>
          ),
        },
      }}
    />
  );
}
